import api from './api';
import { ApiResponse } from '../types';

// ========== Types ==========

export interface ReportPlaceholderDto {
  type: 'QUESTIONNAIRE_INDICATOR' | 'COMPUTED' | 'SYSTEM';
  indicatorId?: number;
  placeholderKey: string;
  displayName: string;
  expression?: string;
  systemMetric?: string;
  dataField: string;
  conclusionRuleId?: number;
  orderNum: number;
}

export interface SaveReportTemplateRequest {
  title: string;
  content: string;
  placeholders: ReportPlaceholderDto[];
}

export interface PlaceholderResponse {
  id: number;
  type: string;
  indicatorId?: number;
  indicatorName?: string;
  placeholderKey: string;
  displayName: string;
  expression?: string;
  systemMetric?: string;
  dataField: string;
  conclusionRuleId?: number;
  orderNum: number;
}

/** 报告模板范围：市级 / 校级 */
export type TemplateScope = 'CITY' | 'SCHOOL';

export interface ReportTemplateResponse {
  id: number;
  questionnaireId: number;
  title: string;
  content: string;
  templateScope?: TemplateScope;
  placeholders: PlaceholderResponse[];
}

export interface ConclusionInfo {
  ruleId: number;
  title: string;
  color: string;
}

export interface AvailableIndicatorResponse {
  category: string;
  indicatorId?: number;
  name: string;
  suggestedPlaceholderKey: string;
  availableDataFields: string[];
  conclusions?: ConclusionInfo[];
}

export interface ChartDataItem {
  name: string;
  /** 学校简称（分学校图横轴 tag 用） */
  shortName?: string;
  count: number;
  percentage: number;
  color: string;
  /** 平均分（分学校平均分对比图用；其它图为 null） */
  value?: number | null;
  /** 是否本校（校级报告高亮本校；市级恒 false） */
  self?: boolean;
}

/** 堆叠柱里的一段（一个档位） */
export interface StackSegment {
  label: string;
  /** 档位排序（规则 orderNum，越大越严重） */
  order: number;
  count: number;
  /** 该校内该档占比%（0-100） */
  percentage: number;
  /** 档位颜色（渐变蓝） */
  color: string;
}

/** 一所学校的堆叠柱 */
export interface SchoolStack {
  school: string;
  /** 学校简称（图表横轴用） */
  shortName?: string;
  self: boolean;
  total: number;
  /** 各「有问题」档位分段（已剔除「无/健康」档；占比=占全校%，各段和≤100） */
  segments: StackSegment[];
}

/** 预警名单一行（一名进入预警的学生） */
export interface WarningRow {
  code: string;          // 唯一编码（学号）
  name: string;          // 学生姓名
  depression: number;    // 抑郁分
  anxiety: number;       // 焦虑分
  selfHarm: number;      // 自伤风险分
  bullying: number;      // 受欺凌分
  academic: number;      // 学业适应风险分
  environment: number;   // 学校环境风险分
  warningIndex: number;  // 预警指数
  triggeredCount: number;
}

export interface PlaceholderValueDto {
  key: string;
  displayName: string;
  resolvedValue: string | null;
  placeholderType:
    | 'TEXT'
    | 'PIE_CHART'
    | 'BAR_CHART'
    | 'BY_SCHOOL_BAR_CHART'
    | 'BY_SCHOOL_STACK_CHART'
    | 'WARNING_LIST_TABLE';
  chartData: ChartDataItem[] | null;
  /** 分学校堆叠图数据（仅 BY_SCHOOL_STACK_CHART 使用） */
  stackData?: SchoolStack[] | null;
  /** 预警名单（仅 WARNING_LIST_TABLE 使用） */
  warningData?: WarningRow[] | null;
  /** 计算口径说明（占比/平均分等文本占位符有值）；前端在数字后以小字脚注展示 */
  basis?: string | null;
}

export interface ReportPreviewResponse {
  templateHtml: string;
  placeholderValues: PlaceholderValueDto[];
}

// ========== Service ==========

export const reportService = {
  /**
   * 获取问卷的报告模板
   */
  getTemplate: async (
    questionnaireId: number,
    templateScope: TemplateScope = 'CITY'
  ): Promise<ReportTemplateResponse | null> => {
    const response = await api.get<ApiResponse<ReportTemplateResponse | null>>(
      `/admin/questionnaires/${questionnaireId}/report`,
      { params: { templateScope } }
    );
    return response.data.data;
  },

  /**
   * 保存报告模板（创建或更新）
   */
  saveTemplate: async (
    questionnaireId: number,
    request: SaveReportTemplateRequest,
    templateScope: TemplateScope = 'CITY'
  ): Promise<ReportTemplateResponse> => {
    const response = await api.put<ApiResponse<ReportTemplateResponse>>(
      `/admin/questionnaires/${questionnaireId}/report`,
      request,
      { params: { templateScope } }
    );
    return response.data.data;
  },

  /**
   * 删除报告模板
   */
  deleteTemplate: async (
    questionnaireId: number,
    templateScope: TemplateScope = 'CITY'
  ): Promise<void> => {
    await api.delete(`/admin/questionnaires/${questionnaireId}/report`, {
      params: { templateScope },
    });
  },

  /**
   * 基于当前问卷的题目和指标生成默认模板（不写库）
   */
  getDefaultTemplate: async (questionnaireId: number): Promise<ReportTemplateResponse> => {
    const response = await api.get<ApiResponse<ReportTemplateResponse>>(
      `/admin/questionnaires/${questionnaireId}/report/default-template`
    );
    return response.data.data;
  },

  /**
   * 获取可用指标列表
   */
  getAvailableIndicators: async (questionnaireId: number): Promise<AvailableIndicatorResponse[]> => {
    const response = await api.get<ApiResponse<AvailableIndicatorResponse[]>>(
      `/admin/questionnaires/${questionnaireId}/report/available-indicators`
    );
    return response.data.data;
  },

  /**
   * 渲染报告预览（可选 scope 过滤）
   */
  getPreview: async (
    questionnaireId: number,
    scope?: { schoolId?: number; classId?: string }
  ): Promise<ReportPreviewResponse> => {
    const params: Record<string, string | number> = {};
    if (scope?.schoolId !== undefined) params.schoolId = scope.schoolId;
    if (scope?.classId) params.classId = scope.classId;
    const response = await api.get<ApiResponse<ReportPreviewResponse>>(
      `/admin/questionnaires/${questionnaireId}/report/preview`,
      { params }
    );
    return response.data.data;
  },

  /**
   * 该问卷下出现过的学校 snapshot
   */
  getScopeSchools: async (questionnaireId: number): Promise<ScopeSchool[]> => {
    const response = await api.get<ApiResponse<ScopeSchool[]>>(
      `/admin/questionnaires/${questionnaireId}/report/scope/schools`
    );
    return response.data.data;
  },

  /**
   * 该问卷+学校下出现过的班级 snapshot
   */
  getScopeClasses: async (questionnaireId: number, schoolId: number): Promise<ScopeClass[]> => {
    const response = await api.get<ApiResponse<ScopeClass[]>>(
      `/admin/questionnaires/${questionnaireId}/report/scope/classes`,
      { params: { schoolId } }
    );
    return response.data.data;
  },
};

export interface ScopeSchool {
  schoolId: number;
  schoolName: string;
}

export interface ScopeClass {
  classId: string;
  className: string;
  grade?: string;
}
